import type { IProfile } from '@setplex/tria-api'

import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { createAction } from 'effector-action'
import { persist } from 'effector-storage/local'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { profilesModel } from '~/entities/profiles'
import {
  emptyPinLockState,
  openPinLockPopup,
} from '~/features/pin-lock/popup/model'
import * as pinlockProfilesFeatureModel from '~/features/pin-lock/profiles'
import { noop } from '~/shared/effector'
import { aye, nay } from '~/shared/helpers'
import { PROFILES } from '~/shared/routes'
import { CREATE_PROFILE_ID } from './index.h'
import {
  buildManagePathWithTail,
  extractManageTail,
  hasRegularProfile,
  navigateEditProfile,
} from './lib'
import { $manageReturnLocation } from './return-path'

export const chooseProfile = createEvent<{ id: number }>()

export const selectedProfileId = createEvent<number>()

export const $selectedProfileId = createStore<number | null>(null)
  .on(selectedProfileId, (_, id) => id)
  .on(profilesModel.$optimisticProfiles, (id, profiles) =>
    profiles.find((profile) => profile.id === id) ? id : 0
  )
  .reset(api.session.signOutFx.doneData)

export const $selectedProfile = combine(
  profilesModel.$optimisticProfiles,
  $selectedProfileId,
  (profiles, selectedProfileId) =>
    profiles.find((profile) => profile.id === selectedProfileId) ?? null
)

export const $isActiveProfileRegular = $selectedProfile.map((profile) =>
  profile ? !profile.kidsProfile : false
)

export const $isActiveKidsProfile = $selectedProfile.map((profile) =>
  Boolean(profile?.kidsProfile)
)

export const $isProfileActionsPinLocked = combine(
  $isActiveProfileRegular,
  pinlockProfilesFeatureModel.$enabled,
  (isActiveProfileRegular, profilePinlockEnabled) =>
    profilePinlockEnabled ? !isActiveProfileRegular : false
)

// Editing is disabled if:
// 1. Active profile is not regular (kids profile)
// 2. Edit profile is not the currently selected profile
// 3. (non-strict only) Edit profile is a kids profile with selected type
const createProfileEditingDisabled = (strict: boolean) =>
  combine(
    $selectedProfile,
    $isActiveProfileRegular,
    (selectedProfile, isActiveProfileRegular) =>
      (editProfile: IProfile | null) => {
        if (!isActiveProfileRegular) {
          // Allow editing own profile
          if (selectedProfile?.id === editProfile?.id) {
            return false
          }
          // Allow editing kids profile without selected type
          if (
            !strict &&
            aye(editProfile?.kidsProfile) &&
            nay(editProfile?.kidsProfileTypeId)
          ) {
            return false
          }
          return true
        }
        return false
      }
  )

export const $profileEditingDisabled = createProfileEditingDisabled(false)
// Used on manage page where we don't want to allow editing kids profiles without type
export const $profileEditingDisabledStrict = createProfileEditingDisabled(true)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $selectedProfileId,
  key: 'tria__selected_profile',
  fail: noop,
})

sample({
  clock: [
    profilesModel.removeProfileFx.doneData,
    profilesModel.removeProfileFx.fail,
  ],
  source: { location: router.$location, back: $manageReturnLocation },
  fn: ({ location, back }) => {
    const tailFromLocation = extractManageTail(location?.pathname)
    const rememberedPath = tailFromLocation || back?.pathname
    const managePath = rememberedPath
      ? buildManagePathWithTail(PROFILES.MANAGE, rememberedPath)
      : PROFILES.MANAGE

    return {
      ...location,
      pathname: managePath,
    }
  },
  target: router.navigatePush,
})
export const handleChooseProfile = createEvent<{ id: number }>()
export const handleKidsType = createEvent<{ id: number }>()
const handleEmptyKidsOneType = createEvent<{ id: number }>()
export const handleEmptyKidsMultipleTypes = createEvent<{ id: number }>()

sample({
  clock: handleChooseProfile,
  source: {
    isProfileActionsPinLocked: $isProfileActionsPinLocked,
    optimisticProfiles: profilesModel.$optimisticProfiles,
  },
  filter: ({ isProfileActionsPinLocked, optimisticProfiles }, { id }) => {
    const selectedProfile = optimisticProfiles.find(
      (profile) => profile.id === id
    )

    return isProfileActionsPinLocked && aye(selectedProfile?.locked)
  },
  fn: (_, { id }) => ({
    ...emptyPinLockState,
    profileUnlock: true,
    id,
  }),
  target: openPinLockPopup,
})

sample({
  clock: pinlockProfilesFeatureModel.setUnlocked,
  source: restore(handleChooseProfile, null),
  filter: (params, unlockedId) => params != null && params.id === unlockedId,
  fn: (params) => params!,
  target: chooseProfile,
})

sample({
  clock: handleChooseProfile,
  source: {
    isProfileActionsPinLocked: $isProfileActionsPinLocked,
    optimisticProfiles: profilesModel.$optimisticProfiles,
  },
  filter: ({ isProfileActionsPinLocked, optimisticProfiles }, { id }) => {
    const selectedProfile = optimisticProfiles.find(
      (profile) => profile.id === id
    )

    return nay(isProfileActionsPinLocked) || nay(selectedProfile?.locked)
  },
  fn: (_, { id }) => ({ id }),
  target: handleKidsType,
})

createAction({
  clock: handleKidsType,
  source: {
    profilesSummaryMeta: profilesModel.$profilesSummaryMeta,
    optimisticProfiles: profilesModel.$optimisticProfiles,
  },
  target: {
    chooseProfile,
    handleEmptyKidsOneType,
    handleEmptyKidsMultipleTypes,
  },
  fn: (target, { optimisticProfiles, profilesSummaryMeta }, clock) => {
    const chosenProfile = optimisticProfiles.find(
      (profile) => profile.id === clock.id
    )
    if (!chosenProfile) {
      return target.chooseProfile(clock)
    }

    const hasKidsProfile = Boolean(chosenProfile.kidsProfile)
    const hasKidsType = Boolean(chosenProfile.kidsProfileTypeId)
    if (!hasKidsProfile || hasKidsType) {
      return target.chooseProfile(clock)
    }

    const kidsTypesCount = profilesSummaryMeta?.kidsProfileTypes?.length ?? 0
    if (kidsTypesCount === 1) {
      return target.handleEmptyKidsOneType(clock)
    }

    if (kidsTypesCount > 1) {
      return target.handleEmptyKidsMultipleTypes(clock)
    }

    return target.chooseProfile(clock)
  },
})

// select different profile, than currently active
sample({
  clock: chooseProfile,
  source: profilesModel.updateProfileActiveStateFx.pending,
  filter: (pending) => !pending,
  fn: (_, { id }) => id,
  target: profilesModel.updateProfileActiveStateFx,
})

sample({
  clock: profilesModel.updateProfileActiveStateFx.done,
  fn: ({ params: id }) => id,
  target: selectedProfileId,
})

export const $showAddRegularProfileButton = combine(
  {
    profilesSummaryMeta: profilesModel.$profilesSummaryMeta,
    selectedProfile: $selectedProfile,
    isActiveProfileRegular: $isActiveProfileRegular,
  },
  ({ profilesSummaryMeta, selectedProfile, isActiveProfileRegular }) => {
    if (selectedProfile && !isActiveProfileRegular) {
      return false
    }

    return (
      aye(profilesSummaryMeta?.remainingRegularProfiles) &&
      profilesSummaryMeta.remainingRegularProfiles > 0
    )
  }
)

export const $showAddKidsProfileButton = combine(
  {
    profilesSummaryMeta: profilesModel.$profilesSummaryMeta,
    profiles: profilesModel.$optimisticProfiles,
    isActiveProfileRegular: $isActiveProfileRegular,
    selectedProfile: $selectedProfile,
  },
  ({
    profilesSummaryMeta,
    profiles,
    selectedProfile,
    isActiveProfileRegular,
  }) => {
    if (selectedProfile && !isActiveProfileRegular) {
      return false
    }

    return Boolean(
      profilesSummaryMeta &&
      profilesSummaryMeta.kidsModuleAvailable &&
      hasRegularProfile(profiles) &&
      profilesSummaryMeta.remainingKidsProfiles &&
      profilesSummaryMeta.remainingKidsProfiles > 0
    )
  }
)

const handleCreateProfile = createEvent<{ kids?: boolean }>()
export const createProfile = createEvent<{ kids?: boolean }>()

sample({
  clock: handleCreateProfile,
  source: $isProfileActionsPinLocked,
  filter: (isProfileActionsPinLocked) => aye(isProfileActionsPinLocked),
  fn: () => ({
    ...emptyPinLockState,
    profileUnlock: true,
    id: CREATE_PROFILE_ID,
  }),
  target: openPinLockPopup,
})

sample({
  clock: pinlockProfilesFeatureModel.setUnlocked,
  source: restore(handleCreateProfile, null),
  filter: (params, unlockedId) =>
    params != null && unlockedId === CREATE_PROFILE_ID,
  fn: (params) => params!,
  target: createProfile,
})

sample({
  clock: handleCreateProfile,
  source: $isProfileActionsPinLocked,
  filter: (isProfileActionsPinLocked) => nay(isProfileActionsPinLocked),
  fn: (_, params) => params,
  target: createProfile,
})

export const editProfile = createEvent<{ id: number; selectMode?: boolean }>()
const handleEditProfile = createEvent<{ editProfile: IProfile }>()

sample({
  clock: handleEditProfile,
  source: $profileEditingDisabled,
  filter: (profileEditingDisabled, { editProfile }) =>
    !profileEditingDisabled(editProfile),
  fn: (_, { editProfile }) => ({ id: editProfile.id }),
  target: editProfile,
})

sample({
  clock: editProfile,
  source: {
    location: router.$location,
    back: $manageReturnLocation,
  },
  fn: ({ location, back }, { id, selectMode }) =>
    navigateEditProfile({ location, back, id, selectMode }),
  target: router.navigatePush,
})

sample({
  clock: handleEmptyKidsMultipleTypes,
  target: editProfile.prepend((params: { id: number }) => ({
    ...params,
    selectMode: true,
  })),
})

export const updateProfileFeatureFx = attach({
  effect: profilesModel.updateProfileFx,
})

sample({
  clock: handleEmptyKidsOneType,
  source: {
    optimisticProfiles: profilesModel.$optimisticProfiles,
    profilesSummaryMeta: profilesModel.$profilesSummaryMeta,
  },
  filter: ({ profilesSummaryMeta }) =>
    aye(profilesSummaryMeta?.kidsProfileTypes?.length),
  fn: ({ optimisticProfiles, profilesSummaryMeta }, { id }) => {
    const profile = optimisticProfiles.find((profile) => profile.id === id)

    return {
      id: profile!.id,
      name: profile?.name,
      avatarName: profile?.avatarName,
      kidsProfileTypeId: profilesSummaryMeta!.kidsProfileTypes![0].id,
    }
  },
  target: updateProfileFeatureFx,
})

sample({
  clock: updateProfileFeatureFx.done,
  fn: ({ params: id }) => id,
  target: chooseProfile,
})

export const $oneKidsModuleTypeLabel = combine(
  profilesModel.$profilesSummaryMeta,
  (profilesSummaryMeta) =>
    profilesSummaryMeta?.kidsProfileTypes?.length === 1
      ? (profilesSummaryMeta?.kidsProfileTypes?.[0]?.title ?? null)
      : null
)

register($isActiveProfileRegular, '$features/profiles/isActiveProfileRegular')
register($isActiveKidsProfile, '$features/profiles/isActiveKidsProfile')
register($showAddRegularProfileButton, '$profiles/showAddRegularProfileButton')
register($showAddKidsProfileButton, '$profiles/showAddKidsProfileButton')
register(handleChooseProfile, 'features/profiles/chooseProfile')
register(handleCreateProfile, 'features/profiles/createProfile')
register(handleEditProfile, 'features/profiles/editProfile')
register($oneKidsModuleTypeLabel, '$features/profiles/oneKidsModuleTypeLabel')
